import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "react-query";
import { workspaceScopedQueryKey } from "../utils/workspaceQueryScope";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import { Snackbar, Alert, Box, Button, Stack, Typography } from "@mui/material";
import {
  NOTIFICATION_TYPES,
  isNotificationType,
  DEFAULT_DEDUPE_TTL_MS,
} from "../notifications/types";
import {
  buildDefaultNotificationPrefs,
  normalizePrefsPartial,
  parseNotificationPrefsFromApi,
} from "../notifications/prefsUtils";
import {
  NOTIFICATION_PREFS_KEY,
  NOTIFICATION_INBOX_LOCAL_KEY,
  NOTIFICATION_BANNER_DISMISS_KEY,
  NOTIFICATION_AUDIO_UNLOCK_DISMISS_KEY,
  notificationInboxStorageKey,
} from "../notifications/storageKeys";
import { getActiveWorkspaceSlug } from "../utils/workspaceStorage";
import { fetchData } from "../api/client";
import { readTabBearerToken } from "../utils/tabAuthStorage";
import {
  playAlertSoundForType,
  unlockNotificationAudio,
} from "../notifications/soundUtils";
import {
  shouldShowDesktopNotification,
  showDesktopNotification,
} from "../notifications/desktopNotification";
import { useAutoWebPushRegister } from "../hooks/useAutoWebPushRegister";
import { autoRegisterWebPushIfPermitted } from "../notifications/webPushClient";
import { resolveTeamNotificationDisplay } from "../notifications/teamNotificationDisplay";
import {
  appendOperatorNotification,
  fetchOperatorNotifications,
  getRequestDetails,
  getConversationById,
  markOperatorNotificationsRead,
  markOperatorNotificationsReadByDeepLink,
} from "../api/client";
import { useSocketContext } from "./SocketContext";
import {
  parseTeamSpaceIdFromLocation,
} from "../pages/Team/utils/teamUiUtils";

const BATCH_MS = 3200;
/** Agrega enfileiramentos de voz por canal — operações intensas não geram toast por ligação. */
const VOICE_QUEUE_BATCH_MS = 3800;
const SOUND_COOLDOWN_MS = 2800;
/** Notificações do servidor mais antigas que isso não disparam som na sincronização inicial. */
const SERVER_SOUND_MAX_AGE_MS = 120_000;
const MAX_INBOX_LOCAL = 80;
const MAX_TOASTS = 4;
const REQUEST_META_TTL_MS = 10 * 60 * 1000;
const ASSIGNMENT_CACHE_TTL_MS = 45_000;

/**
 * Mesma regra conceitual de `emitMessageSocketToLeadUsers` (xpro4api): com responsável humano,
 * só ele deve ser alertado; em fila (sem humano/bot/IA como “dono”) o socket já vai para quem tem permissão.
 */
function shouldNotifyChatMessageForCurrentUser(assignment, currentUserId) {
  if (currentUserId == null || !Number.isFinite(Number(currentUserId))) {
    return true;
  }
  if (!assignment) {
    return true;
  }
  const me = Number(currentUserId);
  const assigneeId =
    assignment.userId != null && Number(assignment.userId) > 0
      ? Number(assignment.userId)
      : null;
  const assigneeUra = Number(assignment.userUra) || 0;
  const assigneeIsHuman = assigneeId != null && assigneeUra !== 1;

  const rawAgent =
    assignment.agentId != null && assignment.agentId !== ""
      ? String(assignment.agentId).trim()
      : "";
  const hasAiAgent = rawAgent !== "" && rawAgent !== "0";
  const assigneeIsChatbot = assigneeId != null && assigneeUra === 1;

  const isQueueForNotifications =
    !assigneeIsHuman && !assigneeIsChatbot && !hasAiAgent;

  if (assigneeIsHuman && assigneeId) {
    return assigneeId === me;
  }
  if (isQueueForNotifications) {
    return true;
  }
  return true;
}

function loadPrefs() {
  try {
    const raw = localStorage.getItem(NOTIFICATION_PREFS_KEY);
    if (!raw) return buildDefaultNotificationPrefs();
    const p = JSON.parse(raw);
    return normalizePrefsPartial(p);
  } catch {
    return buildDefaultNotificationPrefs();
  }
}

function unwrapMePayload(d) {
  return d && typeof d === "object" && "data" in d ? d.data : d;
}

function savePrefs(prefs) {
  try {
    localStorage.setItem(NOTIFICATION_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
}

function loadLocalInbox(workspaceSlug = getActiveWorkspaceSlug()) {
  try {
    const scopedKey = notificationInboxStorageKey(workspaceSlug);
    const raw = scopedKey
      ? localStorage.getItem(scopedKey)
      : localStorage.getItem(NOTIFICATION_INBOX_LOCAL_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveLocalInbox(items, workspaceSlug = getActiveWorkspaceSlug()) {
  try {
    const scopedKey = notificationInboxStorageKey(workspaceSlug);
    const key = scopedKey || NOTIFICATION_INBOX_LOCAL_KEY;
    localStorage.setItem(key, JSON.stringify(items.slice(0, MAX_INBOX_LOCAL)));
  } catch {
    /* ignore */
  }
}

function parseConversationIdFromDeepLink(deepLink) {
  if (!deepLink || typeof deepLink !== "string") return null;
  const m = deepLink.match(/\/chat\/(\d+)/);
  return m ? String(m[1]) : null;
}

function parseSpaceIdFromDeepLink(deepLink) {
  if (!deepLink || typeof deepLink !== "string") return null;
  const m = deepLink.match(/\/equipe\/(\d+)/);
  return m ? String(m[1]) : null;
}

function isLegacyOverdueFlood(entry) {
  if (entry?.type !== NOTIFICATION_TYPES.task_overdue && entry?.event_type !== NOTIFICATION_TYPES.task_overdue) {
    return false;
  }
  const link = String(entry.deepLink || entry.deep_link || "");
  if (link.includes("overdue=1")) return false;
  if (/[?&]card=/.test(link)) return false;
  return true;
}

function countInboxUnread(entries) {
  let count = 0;
  let overdueUnread = false;
  for (const entry of entries || []) {
    if (entry?.type === NOTIFICATION_TYPES.task_overdue) {
      if (!entry.read) overdueUnread = true;
      continue;
    }
    if (!entry?.read) count += 1;
  }
  return count + (overdueUnread ? 1 : 0);
}

function isTeamNotificationType(type) {
  return (
    type === NOTIFICATION_TYPES.team_message || type === NOTIFICATION_TYPES.team_mention
  );
}

function teamDeepLinkForSpace(spaceId) {
  const sid = Number(spaceId);
  if (!Number.isFinite(sid) || sid <= 0) return null;
  return `/equipe/${sid}`;
}

function notificationMatchesTeamSpace(entry, spaceId) {
  const sid = String(spaceId);
  if (!sid || sid === "NaN") return false;
  if (entry?.spaceId != null && String(entry.spaceId) === sid) return true;
  const link = entry?.deepLink || "";
  return link === `/equipe/${sid}` || link.startsWith(`/equipe/${sid}?`);
}

/** Uma entrada por conversa da equipe no inbox (evita badge inflado com N mensagens). */
function collapseTeamNotificationsInInbox(entries) {
  const nonTeam = [];
  const byLink = new Map();

  for (const x of entries) {
    if (!isTeamNotificationType(x.type)) {
      nonTeam.push(x);
      continue;
    }
    const link =
      x.deepLink || (x.spaceId != null ? teamDeepLinkForSpace(x.spaceId) : null);
    if (!link) {
      nonTeam.push(x);
      continue;
    }
    const g = byLink.get(link) || { latest: null, unread: 0, total: 0 };
    g.total += 1;
    if (!x.read) g.unread += 1;
    if (!g.latest || new Date(x.createdAt) > new Date(g.latest.createdAt)) {
      g.latest = x;
    }
    byLink.set(link, g);
  }

  const team = Array.from(byLink.values()).map(({ latest, unread, total }) => ({
    ...latest,
    teamGroupedCount: total,
    teamGroupedUnread: unread,
    read: unread === 0,
  }));

  return [...nonTeam, ...team].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );
}

function absoluteUrlForWindowOpen(link) {
  if (!link || typeof link !== "string") return "";
  try {
    return new URL(link, window.location.origin).href;
  } catch {
    return link;
  }
}

const NotificationContext = createContext(null);

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return ctx;
}

/** Para hooks compartilhados (ex.: useChatState) quando o provider pode não existir em testes. */
export function useNotificationsOptional() {
  return useContext(NotificationContext);
}

function severityForType(type) {
  switch (type) {
    case NOTIFICATION_TYPES.security:
      return "error";
    case NOTIFICATION_TYPES.billing:
      return "warning";
    case NOTIFICATION_TYPES.new_request:
    case NOTIFICATION_TYPES.voice_queue:
    case NOTIFICATION_TYPES.request_assigned:
      return "info";
    case NOTIFICATION_TYPES.team_mention:
      return "warning";
    case NOTIFICATION_TYPES.task_overdue:
      return "error";
    case NOTIFICATION_TYPES.task_due:
    case NOTIFICATION_TYPES.task_completed:
    case NOTIFICATION_TYPES.task_board_invite:
    case NOTIFICATION_TYPES.agenda_reminder:
      return "info";
    default:
      return "success";
  }
}

export function NotificationProvider({ children }) {
  const navigate = useNavigate();
  const { t } = useTranslation("notifications");
  const queryClient = useQueryClient();
  const { socket } = useSocketContext();

  const [prefs, setPrefsState] = useState(loadPrefs);
  const [inbox, setInbox] = useState(loadLocalInbox);
  const [toasts, setToasts] = useState([]);
  const [bannerOpen, setBannerOpen] = useState(false);
  const [audioUnlockOpen, setAudioUnlockOpen] = useState(false);
  /** Indicador persistente (ex.: cor do sino) até salvar com sucesso. */
  const [prefsSyncFailed, setPrefsSyncFailed] = useState(false);
  /** Snackbar pode fechar por tempo sem esconder o estado de falha no sino. */
  const [prefsSyncSnackOpen, setPrefsSyncSnackOpen] = useState(false);

  const dedupeRef = useRef(new Map());
  const lastSoundAtRef = useRef({});
  const serverSoundPlayedRef = useRef(new Set());
  const batchRef = useRef({ timer: null, pending: new Map() });
  const voiceQueueBatchRef = useRef({ timer: null, byChannel: new Map() });
  /** Último alerta emitido por canal (ms) — evita spam em pico; permite novo aviso após quietude. */
  const lastVoiceQueueNotifyAtRef = useRef(new Map());
  const inboxRef = useRef(inbox);
  const requestMetaRef = useRef(new Map()); // conversationId -> { data, fetchedAt }
  const requestMetaInFlightRef = useRef(new Map()); // conversationId -> Promise

  const bearer = typeof window !== "undefined" ? readTabBearerToken() : null;
  const tenantSlug = getActiveWorkspaceSlug();
  useAutoWebPushRegister(Boolean(bearer) && Boolean(tenantSlug));

  useEffect(() => {
    setInbox(loadLocalInbox(tenantSlug));
    assignmentCacheRef.current.clear();
  }, [tenantSlug]);

  const { data: meRes } = useQuery(
    workspaceScopedQueryKey("me"),
    () => fetchData("/users/me"),
    {
      staleTime: 10_000,
      refetchOnWindowFocus: false,
      enabled: Boolean(bearer) && Boolean(tenantSlug),
    }
  );

  const meUserId = useMemo(() => {
    const wrap = meRes || {};
    const inner = unwrapMePayload(wrap.data);
    const user = inner && typeof inner === "object" && inner.id != null ? inner : null;
    if (!user) return null;
    const n = Number(user.id);
    return Number.isFinite(n) ? n : null;
  }, [meRes]);

  const assignmentCacheRef = useRef(new Map());
  const teamFocusRef = useRef({ pageSpaceId: null, floatingIds: new Set() });
  const serverInboxMergeTimerRef = useRef(null);
  /** Evita rajadas de POST mark-read-by-link (mesmo deep_link / espaço). */
  const markReadByLinkInFlightRef = useRef(new Map());
  const markReadByLinkCooldownRef = useRef(new Map());
  const dismissTeamDebounceRef = useRef(new Map());

  const loadConversationAssignment = useCallback(async (conversationId) => {
    const cid = String(conversationId || "").trim();
    if (!cid) return null;
    const ws = getActiveWorkspaceSlug() || "";
    const cacheKey = `${ws}:${cid}`;
    const now = Date.now();
    const hit = assignmentCacheRef.current.get(cacheKey);
    if (hit && now - hit.at < ASSIGNMENT_CACHE_TTL_MS) {
      return hit;
    }
    try {
      const raw = await getConversationById(cid);
      const payload = raw?.data ?? raw;
      const row = {
        userId: payload?.userId ?? null,
        userUra: payload?.userUra ?? null,
        agentId: payload?.agentId ?? null,
        at: now,
      };
      assignmentCacheRef.current.set(cacheKey, row);
      return row;
    } catch {
      return null;
    }
  }, []);

  const didHydrateServerPrefsRef = useRef(false);
  const userEditedPrefsRef = useRef(false);
  const persistPrefsTimerRef = useRef(null);
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;

  useEffect(() => {
    inboxRef.current = inbox;
  }, [inbox]);

  const runPersistPrefs = useCallback(
    async (next) => {
      try {
        await fetchData("/users/me", "PUT", { notification_prefs_json: next });
        queryClient.invalidateQueries(workspaceScopedQueryKey("me"));
        setPrefsSyncFailed(false);
        setPrefsSyncSnackOpen(false);
      } catch {
        setPrefsSyncFailed(true);
        setPrefsSyncSnackOpen(true);
      }
    },
    [queryClient]
  );

  const dismissPrefsSyncError = useCallback(() => {
    setPrefsSyncFailed(false);
    setPrefsSyncSnackOpen(false);
  }, []);

  const retryPrefsSync = useCallback(() => {
    void runPersistPrefs(prefsRef.current);
  }, [runPersistPrefs]);

  const flushPersistPrefsNow = useCallback(async () => {
    if (!persistPrefsTimerRef.current) return;
    clearTimeout(persistPrefsTimerRef.current);
    persistPrefsTimerRef.current = null;
    if (typeof window === "undefined" || !readTabBearerToken()) return;
    await runPersistPrefs(prefsRef.current);
  }, [runPersistPrefs]);

  useEffect(() => {
    return () => {
      if (persistPrefsTimerRef.current) {
        clearTimeout(persistPrefsTimerRef.current);
        persistPrefsTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === "hidden") void flushPersistPrefsNow();
    };
    window.addEventListener("pagehide", flushPersistPrefsNow);
    document.addEventListener("visibilitychange", onHidden);
    return () => {
      window.removeEventListener("pagehide", flushPersistPrefsNow);
      document.removeEventListener("visibilitychange", onHidden);
    };
  }, [flushPersistPrefsNow]);

  const schedulePersistPrefs = useCallback(
    (next) => {
      if (typeof window === "undefined" || !readTabBearerToken()) return;
      if (persistPrefsTimerRef.current) clearTimeout(persistPrefsTimerRef.current);
      persistPrefsTimerRef.current = window.setTimeout(async () => {
        persistPrefsTimerRef.current = null;
        await runPersistPrefs(next);
      }, 700);
    },
    [runPersistPrefs]
  );

  useEffect(() => {
    if (!bearer) {
      didHydrateServerPrefsRef.current = false;
      userEditedPrefsRef.current = false;
      setPrefsSyncFailed(false);
      setPrefsSyncSnackOpen(false);
      return;
    }
    if (didHydrateServerPrefsRef.current) return;
    if (userEditedPrefsRef.current) {
      didHydrateServerPrefsRef.current = true;
      return;
    }
    const wrap = meRes || {};
    const inner = unwrapMePayload(wrap.data);
    const user = inner && typeof inner === "object" && inner.id ? inner : null;
    if (!user) return;
    didHydrateServerPrefsRef.current = true;
    const raw = user.notification_prefs_json;
    if (raw == null || raw === "") return;
    const parsed = parseNotificationPrefsFromApi(raw);
    if (!parsed) return;
    setPrefsState(parsed);
    savePrefs(parsed);
  }, [bearer, meRes]);

  const setPrefs = useCallback(
    (updater) => {
      userEditedPrefsRef.current = true;
      setPrefsState((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        savePrefs(next);
        schedulePersistPrefs(next);
        return next;
      });
    },
    [schedulePersistPrefs]
  );

  const upsertMetaIntoInbox = useCallback((conversationId, meta) => {
    if (!conversationId || !meta) return;
    setInbox((prev) => {
      const next = prev.map((x) => {
        const cid = x.conversationId || parseConversationIdFromDeepLink(x.deepLink);
        if (String(cid || "") !== String(conversationId)) return x;
        if (x.requestMeta && x.requestMeta.protocol === meta.protocol) return x;
        return { ...x, conversationId: String(conversationId), requestMeta: meta };
      });
      saveLocalInbox(next);
      return next;
    });
  }, []);

  const ensureRequestMeta = useCallback(
    async (conversationId) => {
      const cid = String(conversationId || "").trim();
      if (!cid) return null;
      const cacheKey = `${getActiveWorkspaceSlug() || ""}:${cid}`;
      const cached = requestMetaRef.current.get(cacheKey);
      if (cached && Date.now() - cached.fetchedAt < REQUEST_META_TTL_MS) {
        return cached.data;
      }
      if (requestMetaInFlightRef.current.has(cacheKey)) {
        return requestMetaInFlightRef.current.get(cacheKey);
      }
      const p = (async () => {
        try {
          const d = await getRequestDetails(cid);
          const meta = d
            ? {
                protocol: String(d.id || cid),
                clientName: d.cliente_nome ? String(d.cliente_nome) : "",
                typeName: d.tipo ? String(d.tipo) : "",
                subtype1: d.subtipo1 ? String(d.subtipo1) : "",
                subtype2: d.subtipo2 ? String(d.subtipo2) : "",
                subtype3: d.subtipo3 ? String(d.subtipo3) : "",
                originName: d.origem ? String(d.origem) : "",
              }
            : null;
          if (meta) {
            requestMetaRef.current.set(cacheKey, { data: meta, fetchedAt: Date.now() });
            upsertMetaIntoInbox(cid, meta);
          }
          return meta;
        } catch {
          return null;
        } finally {
          requestMetaInFlightRef.current.delete(cacheKey);
        }
      })();
      requestMetaInFlightRef.current.set(cacheKey, p);
      return p;
    },
    [upsertMetaIntoInbox]
  );

  const isTeamSpaceFocused = useCallback((spaceId) => {
    const sid = Number(spaceId);
    if (!Number.isFinite(sid) || sid <= 0) return false;
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return false;
    const focus = teamFocusRef.current;
    if (focus.floatingIds.has(sid)) return true;
    if (typeof window !== "undefined") {
      const activeSid = parseTeamSpaceIdFromLocation(
        window.location.pathname,
        window.location.search
      );
      if (activeSid === sid) return true;
    }
    if (focus.pageSpaceId === sid && typeof window !== "undefined") {
      const path = window.location.pathname;
      return path.includes(`/equipe/${sid}`) || path.includes(`/popup-equipe/${sid}`);
    }
    return false;
  }, []);

  const MARK_READ_BY_LINK_COOLDOWN_MS = 30_000;

  const safeMarkReadByDeepLink = useCallback(async (deepLink) => {
    const link = String(deepLink || "").trim();
    if (!link) return;

    const now = Date.now();
    const cooledUntil = markReadByLinkCooldownRef.current.get(link) || 0;
    if (now < cooledUntil) return;

    const inflight = markReadByLinkInFlightRef.current.get(link);
    if (inflight) return inflight;

    const p = (async () => {
      try {
        await markOperatorNotificationsReadByDeepLink(link);
        markReadByLinkCooldownRef.current.set(link, Date.now() + MARK_READ_BY_LINK_COOLDOWN_MS);
      } catch {
        markReadByLinkCooldownRef.current.delete(link);
      } finally {
        markReadByLinkInFlightRef.current.delete(link);
      }
    })();

    markReadByLinkInFlightRef.current.set(link, p);
    return p;
  }, []);

  const dismissTeamSpaceNotifications = useCallback(
    async (spaceId, { forceServerSync = false } = {}) => {
      const sid = Number(spaceId);
      if (!Number.isFinite(sid) || sid <= 0) return;

      const deepLink = teamDeepLinkForSpace(sid);
      const serverIdsToMark = [];
      let changedLocal = false;

      setInbox((prev) => {
        const next = prev.map((x) => {
          if (x.read || !isTeamNotificationType(x.type)) return x;
          if (!notificationMatchesTeamSpace(x, sid)) return x;
          if (x.serverId != null) serverIdsToMark.push(x.serverId);
          changedLocal = true;
          return { ...x, read: true };
        });
        if (!changedLocal) return prev;
        saveLocalInbox(next);
        return next;
      });

      if (!changedLocal && !forceServerSync) {
        const cooledUntil = deepLink
          ? markReadByLinkCooldownRef.current.get(deepLink) || 0
          : 0;
        if (Date.now() < cooledUntil) return;
      }

      try {
        if (deepLink) {
          await safeMarkReadByDeepLink(deepLink);
        } else if (serverIdsToMark.length) {
          await markOperatorNotificationsRead(serverIdsToMark);
        }
      } catch {
        /* ignore */
      }
    },
    [safeMarkReadByDeepLink]
  );

  const scheduleDismissTeamSpaceNotifications = useCallback(
    (spaceId) => {
      const sid = Number(spaceId);
      if (!Number.isFinite(sid) || sid <= 0) return;
      if (dismissTeamDebounceRef.current.has(sid)) return;
      const timer = window.setTimeout(() => {
        dismissTeamDebounceRef.current.delete(sid);
        void dismissTeamSpaceNotifications(sid);
      }, 900);
      dismissTeamDebounceRef.current.set(sid, timer);
    },
    [dismissTeamSpaceNotifications]
  );

  const setTeamPageFocus = useCallback(
    (spaceId) => {
      const sid = spaceId != null ? Number(spaceId) : null;
      const next =
        sid != null && Number.isFinite(sid) && sid > 0 ? sid : null;
      const prev = teamFocusRef.current.pageSpaceId;
      if (prev === next) return;
      teamFocusRef.current.pageSpaceId = next;
      if (next) {
        void dismissTeamSpaceNotifications(next, { forceServerSync: true });
      }
    },
    [dismissTeamSpaceNotifications]
  );

  const registerTeamFloatingSpace = useCallback(
    (spaceId, active) => {
      const sid = spaceId != null ? Number(spaceId) : null;
      if (sid == null || !Number.isFinite(sid) || sid <= 0) return;
      if (active) {
        const wasActive = teamFocusRef.current.floatingIds.has(sid);
        teamFocusRef.current.floatingIds.add(sid);
        if (!wasActive) {
          void dismissTeamSpaceNotifications(sid, { forceServerSync: true });
        }
      } else {
        teamFocusRef.current.floatingIds.delete(sid);
      }
    },
    [dismissTeamSpaceNotifications]
  );

  useEffect(() => {
    // tenta enriquecer metadados das solicitações visíveis (sem travar a UI)
    const ids = Array.from(
      new Set(
        inbox
          .map((x) => x.conversationId || parseConversationIdFromDeepLink(x.deepLink))
          .filter(Boolean)
          .map(String)
      )
    ).slice(0, 12);
    ids.forEach((cid) => {
      void ensureRequestMeta(cid);
    });
  }, [ensureRequestMeta, inbox]);

  useEffect(() => {
    try {
      if (localStorage.getItem(NOTIFICATION_BANNER_DISMISS_KEY) === "1") return;
      if (Notification.permission === "granted") return;
      setBannerOpen(true);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const onInteract = () => {
      unlockNotificationAudio();
      setAudioUnlockOpen(false);
    };
    window.addEventListener("click", onInteract, { passive: true });
    window.addEventListener("keydown", onInteract, { passive: true });
    window.addEventListener("touchstart", onInteract, { passive: true });
    window.addEventListener("pointerdown", onInteract, { passive: true });
    return () => {
      window.removeEventListener("click", onInteract);
      window.removeEventListener("keydown", onInteract);
      window.removeEventListener("touchstart", onInteract);
      window.removeEventListener("pointerdown", onInteract);
    };
  }, []);

  const shouldPlaySound = useCallback(
    (type, isConversationFocused) => {
      if (!prefs.soundEnabled) return false;
      if (prefs.soundOnlyBackground) {
        if (document.visibilityState === "visible" && isConversationFocused) return false;
      }
      const now = Date.now();
      const last = lastSoundAtRef.current[type] || 0;
      if (now - last < SOUND_COOLDOWN_MS) return false;
      return true;
    },
    [prefs.soundEnabled, prefs.soundOnlyBackground]
  );

  const playSoundForType = useCallback(async (type) => {
    if (type === NOTIFICATION_TYPES.call_incoming) return false;
    const played = await playAlertSoundForType(type);
    if (played) {
      lastSoundAtRef.current[type] = Date.now();
    }
    return played;
  }, []);

  const maybePromptAudioUnlock = useCallback(() => {
    try {
      if (sessionStorage.getItem(NOTIFICATION_AUDIO_UNLOCK_DISMISS_KEY) === "1") return;
    } catch {
      /* ignore */
    }
    setAudioUnlockOpen(true);
  }, []);

  const tryPlayNotificationSound = useCallback(
    async (type, isContentFocused) => {
      if (!shouldPlaySound(type, isContentFocused)) return;
      const played = await playSoundForType(type);
      if (!played) maybePromptAudioUnlock();
    },
    [maybePromptAudioUnlock, playSoundForType, shouldPlaySound]
  );

  const dismissAudioUnlock = useCallback(() => {
    try {
      sessionStorage.setItem(NOTIFICATION_AUDIO_UNLOCK_DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setAudioUnlockOpen(false);
  }, []);

  const activateAudioUnlock = useCallback(() => {
    unlockNotificationAudio();
    void playAlertSoundForType(NOTIFICATION_TYPES.chat_message).then((ok) => {
      if (ok) dismissAudioUnlock();
    });
  }, [dismissAudioUnlock]);

  const mergeServerNotifications = useCallback(async () => {
    if (!tenantSlug) return;
    try {
      const rows = await fetchOperatorNotifications(50);
      if (!Array.isArray(rows) || rows.length === 0) return;
      const autoReadLinks = new Set();
      const prevIds = new Set(inboxRef.current.map((x) => x.id));
      const soundCandidates = [];

      for (const r of rows) {
        const id = `srv-${r.id}`;
        const cid = parseConversationIdFromDeepLink(r.deep_link);
        const sid = parseSpaceIdFromDeepLink(r.deep_link);
        const evType = isNotificationType(r.event_type)
          ? r.event_type
          : NOTIFICATION_TYPES.system;
        const focusedTeam =
          sid && isTeamNotificationType(evType) && isTeamSpaceFocused(sid);
        const read = Boolean(r.read_at) || focusedTeam;
        if (focusedTeam && r.deep_link) autoReadLinks.add(r.deep_link);

        if (isLegacyOverdueFlood({ ...r, type: evType, deepLink: r.deep_link })) continue;

        if (!prevIds.has(id) && !read) {
          const categoryKey =
            evType === NOTIFICATION_TYPES.response_sla
              ? NOTIFICATION_TYPES.new_request
              : evType === "voice_queue_callback"
                ? NOTIFICATION_TYPES.voice_queue
                : evType;
          if (prefs.categories[categoryKey] === false) continue;
          const createdAtMs = r.created_at ? new Date(r.created_at).getTime() : Date.now();
          if (Date.now() - createdAtMs <= SERVER_SOUND_MAX_AGE_MS) {
            const isConvFocused =
              Boolean(cid) &&
              typeof window !== "undefined" &&
              window.location.pathname.includes(`/chat/${cid}`);
            const isTeamFocused = sid ? isTeamSpaceFocused(sid) : false;
            const tabVisible =
              typeof document !== "undefined" && document.visibilityState === "visible";
            soundCandidates.push({
              id,
              type:
                evType === NOTIFICATION_TYPES.response_sla
                  ? NOTIFICATION_TYPES.new_request
                  : evType,
              isContentFocused:
                isConvFocused ||
                isTeamFocused ||
                (evType === NOTIFICATION_TYPES.voice_queue && tabVisible),
            });
          }
        }
      }

      setInbox((prev) => {
        const byId = new Map(prev.map((x) => [x.id, x]));
        for (const r of rows) {
          const id = `srv-${r.id}`;
          const cid = parseConversationIdFromDeepLink(r.deep_link);
          const sid = parseSpaceIdFromDeepLink(r.deep_link);
          const evType = isNotificationType(r.event_type)
            ? r.event_type
            : NOTIFICATION_TYPES.system;
          const focusedTeam =
            sid &&
            isTeamNotificationType(evType) &&
            isTeamSpaceFocused(sid);
          const read = Boolean(r.read_at) || focusedTeam;

          if (isLegacyOverdueFlood({ ...r, type: evType, deepLink: r.deep_link })) {
            continue;
          }

          if (!byId.has(id)) {
            byId.set(id, {
              id,
              serverId: r.id,
              type: evType,
              title: r.title || "",
              body: r.body || "",
              deepLink: r.deep_link || null,
              conversationId: cid,
              spaceId: sid,
              read,
              createdAt: r.created_at || new Date().toISOString(),
              openLinkInNewTab: evType === NOTIFICATION_TYPES.legacy_socket,
            });
          } else if (focusedTeam && !byId.get(id).read) {
            byId.set(id, { ...byId.get(id), read: true });
          }
        }
        const merged = collapseTeamNotificationsInInbox(
          Array.from(byId.values()).sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          )
        );
        saveLocalInbox(merged);
        return merged.slice(0, MAX_INBOX_LOCAL);
      });

      for (const item of soundCandidates) {
        if (serverSoundPlayedRef.current.has(item.id)) continue;
        serverSoundPlayedRef.current.add(item.id);
        void tryPlayNotificationSound(item.type, item.isContentFocused);
      }

      await Promise.all(
        Array.from(autoReadLinks).map((link) => safeMarkReadByDeepLink(link))
      );
    } catch {
      /* offline / rota ainda indisponível */
    }
  }, [isTeamSpaceFocused, prefs.categories, safeMarkReadByDeepLink, tryPlayNotificationSound, tenantSlug]);

  const scheduleMergeServerNotifications = useCallback(() => {
    if (serverInboxMergeTimerRef.current) {
      clearTimeout(serverInboxMergeTimerRef.current);
    }
    serverInboxMergeTimerRef.current = window.setTimeout(() => {
      serverInboxMergeTimerRef.current = null;
      void mergeServerNotifications();
    }, 600);
  }, [mergeServerNotifications]);

  useEffect(() => {
    if (!tenantSlug) return undefined;
    void mergeServerNotifications();
    return undefined;
  }, [mergeServerNotifications, tenantSlug]);

  const pushToast = useCallback((item) => {
    const id = item.id || `toast-${Date.now()}`;
    setToasts((prev) => {
      const next = [...prev, { ...item, id, open: true }];
      return next.slice(-MAX_TOASTS);
    });
    window.setTimeout(() => {
      setToasts((prev) => prev.map((x) => (x.id === id ? { ...x, open: false } : x)));
    }, 5200);
  }, []);

  const dispatchRaw = useCallback(
    (payload) => {
      const {
        type: rawType,
        title,
        body = "",
        conversationId,
        spaceId,
        deepLink,
        dedupeKey,
        desktopTag,
        skipSound,
        skipDesktop,
        skipToast = false,
        skipServerAppend = false,
        skipInbox = false,
        replaceTaskOverdue = false,
        openLinkInNewTab = false,
        forceContentFocused = false,
      } = payload;

      const type = isNotificationType(rawType) ? rawType : NOTIFICATION_TYPES.system;
      if (prefs.categories[type] === false) return;

      const sid =
        spaceId != null
          ? String(spaceId)
          : parseSpaceIdFromDeepLink(deepLink);
      const key =
        dedupeKey || `${type}-${conversationId || ""}-${sid || ""}-${title}`;
      const now = Date.now();
      const prevT = dedupeRef.current.get(key);
      if (prevT && now - prevT < DEFAULT_DEDUPE_TTL_MS) return;
      dedupeRef.current.set(key, now);

      const isConvFocused =
        Boolean(conversationId) &&
        typeof window !== "undefined" &&
        window.location.pathname.includes(`/chat/${conversationId}`);
      const isTeamFocused = sid ? isTeamSpaceFocused(sid) : false;
      const isContentFocused = Boolean(forceContentFocused) || isConvFocused || isTeamFocused;

      const id = `local-${now}-${Math.random().toString(36).slice(2, 9)}`;
      const link =
        deepLink ||
        (sid ? `/equipe/${sid}` : null) ||
        (conversationId ? `/chat/${conversationId}` : null);

      const cid = conversationId != null ? String(conversationId) : parseConversationIdFromDeepLink(link);
      const metaCacheKey = cid ? `${getActiveWorkspaceSlug() || ""}:${cid}` : null;
      const cachedMeta = metaCacheKey ? requestMetaRef.current.get(metaCacheKey)?.data : null;
      const entry = {
        id,
        type,
        title,
        body,
        deepLink: link,
        conversationId: cid,
        spaceId: sid,
        requestMeta: cachedMeta || null,
        read: false,
        createdAt: new Date().toISOString(),
        openLinkInNewTab: Boolean(openLinkInNewTab),
      };

      if (!skipInbox) {
        setInbox((prev) => {
          const base =
            replaceTaskOverdue || type === NOTIFICATION_TYPES.task_overdue
              ? prev.filter((item) => item.type !== NOTIFICATION_TYPES.task_overdue)
              : prev;
          const next = [entry, ...base].slice(0, MAX_INBOX_LOCAL);
          saveLocalInbox(next);
          return next;
        });
      }

      if (!skipToast) {
        pushToast({
          id,
          severity: severityForType(type),
          title,
          message: body,
          deepLink: link,
          openLinkInNewTab: Boolean(openLinkInNewTab),
        });
      }

      if (cid) {
        void ensureRequestMeta(cid);
      }

      if (!skipSound) {
        void tryPlayNotificationSound(type, isContentFocused);
      }

      const wantDesktop = shouldShowDesktopNotification({
        desktopEnabled: prefs.desktopEnabled,
        skipDesktop,
        isContentFocused,
      });

      if (wantDesktop) {
        void showDesktopNotification({
          title,
          body,
          tag: desktopTag || key,
          url: link || "/",
          onNavigate: link
            ? () => {
                if (openLinkInNewTab) {
                  const url = absoluteUrlForWindowOpen(link);
                  if (url) window.open(url, "_blank", "noopener,noreferrer");
                } else {
                  navigate(link);
                }
              }
            : undefined,
        });
      }

      if (!skipServerAppend) {
        appendOperatorNotification({
          event_type: type,
          title,
          body,
          deep_link: link,
        }).catch(() => {});
      }
    },
    [
      ensureRequestMeta,
      isTeamSpaceFocused,
      navigate,
      prefs,
      pushToast,
      tryPlayNotificationSound,
    ]
  );

  const lastMetaRef = useRef(new Map());

  const flushBatch = useCallback(() => {
    const run = async () => {
      const { pending } = batchRef.current;
      batchRef.current.timer = null;
      if (!pending.size) return;

      const convIds = [...pending.keys()];
      pending.clear();

      const filtered = [];
      for (const cid of convIds) {
        const meta = lastMetaRef.current.get(cid) || {};
        lastMetaRef.current.delete(cid);
        const ev = meta.eventName || "message";

        if (ev === "request") {
          filtered.push({ cid, meta });
          continue;
        }
        const assignment = await loadConversationAssignment(cid);
        if (shouldNotifyChatMessageForCurrentUser(assignment, meUserId)) {
          filtered.push({ cid, meta });
        }
      }

      if (!filtered.length) return;

      if (filtered.length === 1) {
        const { cid, meta } = filtered[0];
        if (meta.isConversationSelected && document.visibilityState === "visible") {
          return;
        }

        const ev = meta.eventName || "message";
        const type =
          ev === "request" ? NOTIFICATION_TYPES.new_request : NOTIFICATION_TYPES.chat_message;
        dispatchRaw({
          type,
          title:
            type === NOTIFICATION_TYPES.new_request
              ? t("titles.newRequest")
              : t("titles.newMessage"),
          body: t("bodies.singleConversation", { id: cid }),
          conversationId: cid,
          dedupeKey: `batch-${type}-${cid}`,
        });
        return;
      }

      const requestItems = filtered.filter(
        (x) => (x.meta.eventName || "message") === "request"
      );
      const chatItems = filtered.filter(
        (x) => (x.meta.eventName || "message") !== "request"
      );

      for (const { cid, meta } of requestItems) {
        if (meta.isConversationSelected && document.visibilityState === "visible") {
          continue;
        }
        dispatchRaw({
          type: NOTIFICATION_TYPES.new_request,
          title: t("titles.newRequest"),
          body: t("bodies.singleConversation", { id: cid }),
          conversationId: cid,
          dedupeKey: `batch-${NOTIFICATION_TYPES.new_request}-${cid}`,
        });
      }

      if (!chatItems.length) return;

      if (chatItems.length === 1) {
        const { cid, meta } = chatItems[0];
        if (meta.isConversationSelected && document.visibilityState === "visible") {
          return;
        }
        dispatchRaw({
          type: NOTIFICATION_TYPES.chat_message,
          title: t("titles.newMessage"),
          body: t("bodies.singleConversation", { id: cid }),
          conversationId: cid,
          dedupeKey: `batch-${NOTIFICATION_TYPES.chat_message}-${cid}`,
        });
        return;
      }

      const chatIds = chatItems.map((x) => x.cid);
      dispatchRaw({
        type: NOTIFICATION_TYPES.chat_message,
        title: t("titles.batchTitle"),
        body: t("bodies.multiConversations", { count: chatIds.length }),
        dedupeKey: `batch-multi-${chatIds.sort().join(",")}-${Date.now()}`,
      });
    };

    void run();
  }, [dispatchRaw, t, loadConversationAssignment, meUserId]);

  const dispatchTeamFromSocket = useCallback(
    (data) => {
      const spaceId = data?.spaceId != null ? Number(data.spaceId) : null;
      if (!spaceId) return;

      // Recibos, reações e sync de thread — não geram alerta (evita beeps/toasts espúrios).
      if (data?.reactionUpdate || data?.syncOnly) return;
      if (data?.receiptUpdate && !data?.eventType) return;

      const rawEventType = data?.eventType;
      const isMentionEvent = rawEventType === NOTIFICATION_TYPES.team_mention;
      const isMessageEvent = rawEventType === NOTIFICATION_TYPES.team_message;
      if (!isMentionEvent && !isMessageEvent) return;

      const authorId = data?.authorId != null ? Number(data.authorId) : null;
      if (authorId && meUserId && authorId === meUserId) return;

      const eventType = isMentionEvent
        ? NOTIFICATION_TYPES.team_mention
        : NOTIFICATION_TYPES.team_message;

      if (isTeamSpaceFocused(spaceId)) {
        scheduleDismissTeamSpaceNotifications(spaceId);
        return;
      }

      const { title, body } = resolveTeamNotificationDisplay({ data, eventType, t });
      const tabVisible =
        typeof document !== "undefined" && document.visibilityState === "visible";
      const isMention = eventType === NOTIFICATION_TYPES.team_mention;

      dispatchRaw({
        type: eventType,
        title,
        body,
        spaceId,
        deepLink: `/equipe/${spaceId}`,
        dedupeKey: `team-${spaceId}-${data?.messageId || ""}`,
        skipServerAppend: true,
        skipInbox: true,
        skipToast: tabVisible && !isMention,
        skipDesktop: isTeamSpaceFocused(spaceId),
      });
      scheduleMergeServerNotifications();
    },
    [
      dispatchRaw,
      isTeamSpaceFocused,
      meUserId,
      scheduleDismissTeamSpaceNotifications,
      scheduleMergeServerNotifications,
      t,
    ]
  );

  const dispatchTaskMentionFromSocket = useCallback(
    (data) => {
      const authorId = data?.authorId != null ? Number(data.authorId) : null;
      if (authorId && meUserId && authorId === meUserId) return;

      const deepLink = data?.deepLink || null;
      const boardId = data?.boardId != null ? Number(data.boardId) : null;
      const cardId = data?.cardId != null ? Number(data.cardId) : null;
      const tabVisible =
        typeof document !== "undefined" && document.visibilityState === "visible";
      const onSameTask =
        boardId &&
        cardId &&
        typeof window !== "undefined" &&
        window.location.pathname.includes(`/tarefas/${boardId}`) &&
        window.location.search.includes(`card=${cardId}`);

      dispatchRaw({
        type: NOTIFICATION_TYPES.team_mention,
        title: data?.title || t("titles.taskMention"),
        body: data?.body || "",
        deepLink,
        dedupeKey: `task-mention-${boardId}-${cardId}-${data?.commentId || ""}`,
        skipServerAppend: true,
        skipInbox: false,
        skipToast: tabVisible && onSameTask,
        skipDesktop: onSameTask,
      });
      scheduleMergeServerNotifications();
    },
    [dispatchRaw, meUserId, scheduleMergeServerNotifications, t]
  );

  const dispatchBoardInviteFromSocket = useCallback(
    (data) => {
      const authorId = data?.authorId != null ? Number(data.authorId) : null;
      if (authorId && meUserId && authorId === meUserId) return;

      const deepLink = data?.deepLink || null;
      const boardId = data?.boardId != null ? Number(data.boardId) : null;
      const tabVisible =
        typeof document !== "undefined" && document.visibilityState === "visible";
      const onSameBoard =
        boardId &&
        typeof window !== "undefined" &&
        window.location.pathname.includes(`/tarefas/${boardId}`);

      dispatchRaw({
        type: NOTIFICATION_TYPES.task_board_invite,
        title: data?.title || t("titles.boardInvite"),
        body: data?.body || "",
        deepLink,
        dedupeKey: `task-board-invite-${boardId || 0}`,
        skipServerAppend: true,
        skipInbox: false,
        skipToast: tabVisible && onSameBoard,
        skipDesktop: onSameBoard,
      });
      scheduleMergeServerNotifications();
    },
    [dispatchRaw, meUserId, scheduleMergeServerNotifications, t]
  );

  const dispatchTaskCompletedFromSocket = useCallback(
    (data) => {
      const authorId = data?.authorId != null ? Number(data.authorId) : null;
      if (authorId && meUserId && authorId === meUserId) return;

      const deepLink = data?.deepLink || null;
      const boardId = data?.boardId != null ? Number(data.boardId) : null;
      const cardId = data?.cardId != null ? Number(data.cardId) : null;
      const leadId = data?.leadId != null ? Number(data.leadId) : null;
      const tabVisible =
        typeof document !== "undefined" && document.visibilityState === "visible";
      const onSameTask =
        boardId &&
        cardId &&
        typeof window !== "undefined" &&
        window.location.pathname.includes(`/tarefas/${boardId}`) &&
        window.location.search.includes(`card=${cardId}`);

      dispatchRaw({
        type: NOTIFICATION_TYPES.task_completed,
        title: data?.title || t("titles.taskCompleted"),
        body: data?.body || "",
        deepLink,
        conversationId: leadId ? String(leadId) : undefined,
        dedupeKey: `task-completed-${boardId || leadId || 0}-${cardId || ""}`,
        skipServerAppend: true,
        skipInbox: false,
        skipToast: tabVisible && onSameTask,
        skipDesktop: onSameTask,
      });
      scheduleMergeServerNotifications();
    },
    [dispatchRaw, meUserId, scheduleMergeServerNotifications, t]
  );

  useEffect(() => {
    if (!socket) return;
    const handleTeamMessage = (payload) => {
      let data = payload;
      if (typeof payload === "string") {
        try {
          data = JSON.parse(payload);
        } catch {
          return;
        }
      }
      dispatchTeamFromSocket(data || {});
    };
    const handleTaskMention = (payload) => {
      let data = payload;
      if (typeof payload === "string") {
        try {
          data = JSON.parse(payload);
        } catch {
          return;
        }
      }
      dispatchTaskMentionFromSocket(data || {});
    };
    const handleTaskCompleted = (payload) => {
      let data = payload;
      if (typeof payload === "string") {
        try {
          data = JSON.parse(payload);
        } catch {
          return;
        }
      }
      dispatchTaskCompletedFromSocket(data || {});
    };
    const handleBoardInvite = (payload) => {
      let data = payload;
      if (typeof payload === "string") {
        try {
          data = JSON.parse(payload);
        } catch {
          return;
        }
      }
      dispatchBoardInviteFromSocket(data || {});
    };
    socket.on("team_message", handleTeamMessage);
    socket.on("team_task_mention", handleTaskMention);
    socket.on("task_completed", handleTaskCompleted);
    socket.on("task_board_invite", handleBoardInvite);
    return () => {
      socket.off("team_message", handleTeamMessage);
      socket.off("team_task_mention", handleTaskMention);
      socket.off("task_completed", handleTaskCompleted);
      socket.off("task_board_invite", handleBoardInvite);
    };
  }, [socket, dispatchTeamFromSocket, dispatchTaskMentionFromSocket, dispatchTaskCompletedFromSocket, dispatchBoardInviteFromSocket]);

  const flushVoiceQueueBatch = useCallback(() => {
    const batch = voiceQueueBatchRef.current;
    batch.timer = null;
    const entries = [...batch.byChannel.entries()];
    batch.byChannel.clear();
    if (!entries.length) return;

    const tabVisible =
      typeof document !== "undefined" && document.visibilityState === "visible";
    const now = Date.now();
    /** Com a janela aberta: no máximo 1 toast a cada 10s por canal (o painel já mostra a fila). */
    const VISIBLE_TOAST_GAP_MS = 10_000;

    for (const [channelKey, meta] of entries) {
      const waiting = Math.max(1, Number(meta.waitingTotal) || 1);
      const lastAt = lastVoiceQueueNotifyAtRef.current.get(channelKey) || 0;

      if (tabVisible && lastAt && now - lastAt < VISIBLE_TOAST_GAP_MS && waiting > 1) {
        continue;
      }
      lastVoiceQueueNotifyAtRef.current.set(channelKey, now);

      const channelHint = meta.channelName ? ` (${meta.channelName})` : "";
      const title =
        waiting <= 1
          ? t("titles.voiceQueue", { defaultValue: "Cliente na linha" })
          : t("titles.voiceQueueMulti", { defaultValue: "Clientes na linha" });
      const body =
        waiting <= 1
          ? t("bodies.voiceQueueSingle", {
              channel: channelHint,
              defaultValue: `Alguém espera no telefone${channelHint}. Fale agora se puder.`,
            })
          : t("bodies.voiceQueueMulti", {
              count: waiting,
              channel: channelHint,
              defaultValue: `${waiting} pessoas aguardam no telefone${channelHint}.`,
            });

      const leadId = meta.leadId != null ? Number(meta.leadId) : null;
      const deepLink =
        leadId > 0
          ? `/chat/${leadId}`
          : channelKey && channelKey !== "0"
            ? `/chat?vq=${channelKey}`
            : "/chat";

      dispatchRaw({
        type: NOTIFICATION_TYPES.voice_queue,
        title,
        body,
        conversationId: leadId > 0 ? leadId : undefined,
        deepLink,
        dedupeKey: `voice-queue-ch-${channelKey}-${Math.floor(now / VISIBLE_TOAST_GAP_MS)}`,
        desktopTag: `voice-queue-ch-${channelKey}`,
        skipServerAppend: true,
        skipInbox: true,
        forceContentFocused: tabVisible,
        skipDesktop: tabVisible,
      });
    }
  }, [dispatchRaw, t]);

  const queueVoiceQueueAlert = useCallback(
    (payload) => {
      if (!payload || payload.alert !== true) return;
      if (prefs.categories[NOTIFICATION_TYPES.voice_queue] === false) return;

      const channelKey = String(payload.channelId || payload.queueScope || "general");
      const waitingTotal = Math.max(1, Number(payload.waitingTotal) || 1);
      const map = voiceQueueBatchRef.current.byChannel;
      const prev = map.get(channelKey) || {};
      map.set(channelKey, {
        waitingTotal: Math.max(Number(prev.waitingTotal) || 0, waitingTotal),
        leadId: payload.leadId != null ? Number(payload.leadId) : prev.leadId || null,
        channelName: payload.channelName || prev.channelName || null,
      });

      if (voiceQueueBatchRef.current.timer) {
        clearTimeout(voiceQueueBatchRef.current.timer);
      }
      voiceQueueBatchRef.current.timer = window.setTimeout(
        flushVoiceQueueBatch,
        VOICE_QUEUE_BATCH_MS
      );
    },
    [flushVoiceQueueBatch, prefs.categories]
  );

  useEffect(() => {
    if (!socket) return undefined;
    const handleVoiceQueueUpdate = (raw) => {
      let data = raw;
      if (typeof raw === "string") {
        try {
          data = JSON.parse(raw);
        } catch {
          return;
        }
      }
      // Sem alert: só refresh do painel (MainLayout). Com alert: toast/desktop agregados.
      queueVoiceQueueAlert(data || {});
    };
    socket.on("voiceQueueUpdate", handleVoiceQueueUpdate);
    return () => {
      socket.off("voiceQueueUpdate", handleVoiceQueueUpdate);
      if (voiceQueueBatchRef.current.timer) {
        clearTimeout(voiceQueueBatchRef.current.timer);
        voiceQueueBatchRef.current.timer = null;
      }
    };
  }, [socket, queueVoiceQueueAlert]);

  const queueSocketActivity = useCallback(
    ({ eventName, conversationId, isConversationSelected, notify = true }) => {
      if (eventName === "messagesReload") return;
      if (notify === false) return;
      const cid = String(conversationId);
      if (!cid || cid === "undefined" || cid === "null") return;

      const pending = batchRef.current.pending;
      pending.set(cid, true);
      lastMetaRef.current.set(cid, {
        eventName,
        isConversationSelected: Boolean(isConversationSelected),
      });

      if (batchRef.current.timer) {
        clearTimeout(batchRef.current.timer);
      }
      batchRef.current.timer = window.setTimeout(flushBatch, BATCH_MS);
    },
    [flushBatch]
  );

  const dispatchFromSocketLegacy = useCallback(
    (payload) => {
      const mensagem =
        typeof payload?.mensagem === "string"
          ? payload.mensagem
          : payload?.body || "";
      const link = payload?.link || payload?.deepLink || null;
      dispatchRaw({
        type: NOTIFICATION_TYPES.legacy_socket,
        title: `${t("titles.legacy")}${t("titles.legacyXpro2Suffix")}`,
        body: mensagem.slice(0, 280),
        deepLink: link,
        dedupeKey: `legacy-${link || ""}-${mensagem.slice(0, 40)}`,
        openLinkInNewTab: true,
      });
    },
    [dispatchRaw, t]
  );

  const requestDesktopPermission = useCallback(async () => {
    if (!("Notification" in window)) return "unsupported";
    const r = await Notification.requestPermission();
    if (r === "granted") {
      setPrefs((p) => ({ ...p, desktopEnabled: true }));
      try {
        if ("serviceWorker" in navigator) {
          await navigator.serviceWorker.register("/sw-push.js");
        }
      } catch {
        /* ignore */
      }
      void autoRegisterWebPushIfPermitted();
    }
    return r;
  }, [setPrefs]);

  const markRead = useCallback(async (ids) => {
    const serverIds = ids
      .map((id) => {
        const m = String(id).match(/^srv-(\d+)$/);
        return m ? Number(m[1]) : null;
      })
      .filter((x) => x != null);

    setInbox((prev) => {
      const next = prev.map((x) => (ids.includes(x.id) ? { ...x, read: true } : x));
      saveLocalInbox(next);
      return next;
    });

    if (serverIds.length) {
      try {
        await markOperatorNotificationsRead(serverIds);
      } catch {
        /* ignore */
      }
    }
  }, []);

  const markAllRead = useCallback(async () => {
    let serverIds = [];
    setInbox((prev) => {
      serverIds = prev.map((x) => x.serverId).filter((x) => x != null);
      const next = prev.map((x) => ({ ...x, read: true }));
      saveLocalInbox(next);
      return next;
    });
    if (serverIds.length) {
      try {
        await markOperatorNotificationsRead(serverIds);
      } catch {
        /* ignore */
      }
    }
  }, []);

  const unreadCount = useMemo(() => countInboxUnread(inbox), [inbox]);

  const dismissBanner = useCallback(() => {
    try {
      localStorage.setItem(NOTIFICATION_BANNER_DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setBannerOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      prefs,
      setPrefs,
      inbox,
      unreadCount,
      dispatch: dispatchRaw,
      dispatchRealtimeSocket: queueSocketActivity,
      dispatchFromSocketLegacy,
      setTeamPageFocus,
      registerTeamFloatingSpace,
      markRead,
      markAllRead,
      requestDesktopPermission,
      bannerOpen,
      dismissBanner,
      prefsSyncFailed,
      dismissPrefsSyncError,
      retryPrefsSync,
    }),
    [
      prefs,
      setPrefs,
      inbox,
      unreadCount,
      dispatchRaw,
      queueSocketActivity,
      dispatchFromSocketLegacy,
      setTeamPageFocus,
      registerTeamFloatingSpace,
      markRead,
      markAllRead,
      requestDesktopPermission,
      bannerOpen,
      dismissBanner,
      prefsSyncFailed,
      dismissPrefsSyncError,
      retryPrefsSync,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Snackbar
        open={prefsSyncSnackOpen}
        autoHideDuration={8000}
        onClose={(_, reason) => {
          if (reason === "clickaway" || reason === "timeout") setPrefsSyncSnackOpen(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ bottom: { xs: 96, sm: 24 } }}
      >
        <Alert
          severity="warning"
          variant="filled"
          onClose={() => setPrefsSyncSnackOpen(false)}
          action={
            <Button color="inherit" size="small" sx={{ fontWeight: 700 }} onClick={retryPrefsSync}>
              {t("prefsSync.retry")}
            </Button>
          }
          sx={{ alignItems: "center" }}
        >
          {t("prefsSync.failed")}
        </Alert>
      </Snackbar>
      <Snackbar
        open={audioUnlockOpen}
        autoHideDuration={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ bottom: { xs: 96, sm: 24 } }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={dismissAudioUnlock}
          role="region"
          aria-label={t("audioUnlock.regionAriaLabel")}
          slotProps={{ closeButton: { "aria-label": t("audioUnlock.closeAriaLabel") } }}
          action={
            <Button color="inherit" size="small" sx={{ fontWeight: 700 }} onClick={activateAudioUnlock}>
              {t("audioUnlock.enable")}
            </Button>
          }
          sx={{ alignItems: "center", maxWidth: { xs: "min(calc(100vw - 24px), 100%)", sm: 480 } }}
        >
          {t("audioUnlock.message")}
        </Alert>
      </Snackbar>
      <Snackbar
        open={bannerOpen}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ mt: { xs: 4.5, sm: 5.5 } }}
      >
        <Alert
          severity="info"
          variant="filled"
          icon={<NotificationsActiveOutlinedIcon fontSize="inherit" />}
          onClose={dismissBanner}
          role="region"
          aria-label={t("banner.regionAriaLabel")}
          aria-live="polite"
          slotProps={{ closeButton: { "aria-label": t("banner.closeAriaLabel") } }}
          sx={(theme) => ({
            width: "100%",
            maxWidth: { xs: "min(calc(100vw - 24px), 100%)", sm: 560 },
            alignItems: "flex-start",
            py: 1.35,
            borderRadius: 2,
            ...(theme.palette.mode === "dark"
              ? {
                  bgcolor: theme.palette.info.dark,
                  color: theme.palette.info.contrastText,
                  boxShadow:
                    "0 4px 6px rgba(0,0,0,0.18), 0 14px 36px rgba(0,0,0,0.42), 0 24px 64px rgba(0,0,0,0.35)",
                }
              : {
                  bgcolor: "#d9e8f8",
                  color: "#0d2845",
                  border: "1px solid #b6d0ef",
                  boxShadow:
                    "0 2px 4px rgba(13, 40, 69, 0.06), 0 12px 28px rgba(13, 40, 69, 0.12), 0 20px 48px rgba(13, 40, 69, 0.14)",
                }),
            "& .MuiAlert-icon": {
              color: theme.palette.mode === "dark" ? "inherit" : theme.palette.primary.main,
              opacity: theme.palette.mode === "dark" ? 1 : 0.95,
            },
            "& .MuiAlert-action .MuiIconButton-root": {
              color: theme.palette.mode === "dark" ? "inherit" : "rgba(13, 40, 69, 0.55)",
            },
            "& .MuiAlert-message": {
              width: "100%",
              color: "inherit",
              pt: 0.2,
            },
          })}
        >
          <Typography
            variant="caption"
            sx={(theme) => ({
              fontWeight: 800,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              display: "block",
              color: theme.palette.mode === "dark" ? "inherit" : theme.palette.primary.main,
            })}
          >
            {t("banner.hintLabel")}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.65, lineHeight: 1.55, fontWeight: 500 }}>
            {t("banner.message")}
          </Typography>
          <Typography
            variant="caption"
            sx={(theme) => ({
              mt: 0.65,
              display: "block",
              lineHeight: 1.45,
              opacity: theme.palette.mode === "dark" ? 0.9 : 0.82,
            })}
          >
            {t("banner.optionalNote")}
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ mt: 1.5 }}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Button
              variant="contained"
              size="small"
              color="inherit"
              onClick={async () => {
                await requestDesktopPermission();
                dismissBanner();
              }}
              sx={{
                fontWeight: 700,
                bgcolor: "common.white",
                color: "primary.dark",
                "&:hover": { bgcolor: "grey.100" },
              }}
            >
              {t("banner.enable")}
            </Button>
            <Button
              variant="text"
              size="small"
              color="inherit"
              onClick={() => {
                navigate("/profile#profile-notifications");
                dismissBanner();
              }}
              sx={(theme) => ({
                fontWeight: 600,
                textDecoration: "underline",
                justifyContent: { xs: "center", sm: "flex-start" },
                color: theme.palette.mode === "dark" ? "inherit" : theme.palette.primary.dark,
              })}
            >
              {t("banner.settings")}
            </Button>
          </Stack>
        </Alert>
      </Snackbar>

      <Box
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          zIndex: (theme) => theme.zIndex.snackbar + 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          maxWidth: 380,
          pointerEvents: "none",
          "& > *": { pointerEvents: "auto" },
        }}
      >
        {toasts.map((toast) => (
          <Snackbar
            key={toast.id}
            open={toast.open}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            sx={{ position: "relative", bottom: "auto", right: "auto" }}
          >
            <Alert
              severity={toast.severity || "info"}
              variant="filled"
              onClose={() =>
                setToasts((prev) => prev.filter((x) => x.id !== toast.id))
              }
              onClick={() => {
                if (toast.deepLink) {
                  if (toast.openLinkInNewTab) {
                    const url = absoluteUrlForWindowOpen(toast.deepLink);
                    if (url) window.open(url, "_blank", "noopener,noreferrer");
                  } else {
                    navigate(toast.deepLink);
                  }
                }
                setToasts((prev) => prev.filter((x) => x.id !== toast.id));
              }}
              sx={{ cursor: toast.deepLink ? "pointer" : "default", width: "100%" }}
            >
              <Box component="span" sx={{ fontWeight: 700, display: "block" }}>
                {toast.title}
              </Box>
              {toast.message ? (
                <Box component="span" sx={{ fontSize: "0.875rem", opacity: 0.95 }}>
                  {toast.message}
                </Box>
              ) : null}
            </Alert>
          </Snackbar>
        ))}
      </Box>
    </NotificationContext.Provider>
  );
}
